import { getAccount, switchChain } from 'wagmi/actions';

import { walletWagmiConfig } from '../config/appkitConfig';

type WagmiConfig = typeof walletWagmiConfig;

/**
 * Puts the connected wallet on `chainId` before something is signed or sent
 * there. A wallet already on the chain is left alone (some connectors cannot
 * switch at all, and must not be asked to when there is nothing to do). A
 * refusal becomes a plain instruction naming the network, never wagmi's own
 * error text.
 *
 * Why every signature path needs this: wagmi only ASSERTS the chain for
 * injected wallets, it never switches, and MetaMask refuses typed data whose
 * `domain.chainId` differs from the active chain ("Provided chainId "25" must
 * match the active chainId "4663""). The app now spans three networks, so the
 * wallet is routinely left on one of them by an earlier step: on 2026-09-01 a
 * Robinhood deposit left it on 4663 and the Cronos-anchored login could not be
 * signed again until the user switched by hand.
 */
export async function ensureWalletChain(
    config: WagmiConfig,
    chainId: number,
): Promise<void> {
    try {
        if (getAccount(config).chainId === chainId) return;
        await switchChain(config, { chainId });
    } catch {
        throw new Error(
            `Switch your wallet to the ${chainName(config, chainId)} network to continue.`,
        );
    }
}

/**
 * The chain an EIP-712 payload must be signed on is the one its domain names.
 * Call this right before `signTypedData`; a payload without a domain chain is
 * left to the wallet.
 */
export async function ensureTypedDataChain(typedData: {
    domain?: { chainId?: number | bigint | string | undefined } | undefined;
}): Promise<void> {
    const raw = typedData.domain?.chainId;
    if (raw === undefined || raw === null) return;
    const chainId = Number(raw);
    if (!Number.isFinite(chainId) || chainId <= 0) return;
    await ensureWalletChain(walletWagmiConfig, chainId);
}

function chainName(config: WagmiConfig, chainId: number): string {
    try {
        return (
            config.chains.find((chain) => chain.id === chainId)?.name ??
            `chain ${chainId}`
        );
    } catch {
        return `chain ${chainId}`;
    }
}
